import React from "react";
import { Aurora, Card, PageHeader, Logo } from "./Shell";

/**
 * Referred — Module 15 §15.4 safety-gate exit.
 *
 * Reached when the pre-session check-in indicates the member would be better
 * served by direct human support than a self-guided session. The actual score
 * threshold is engine-internal; we never show numbers to the member or explain
 * the threshold. Disposition is logged on the next session record so the
 * wellness team can follow up.
 */
export default function Referred({ onRestart }) {
  return (
    <Aurora>
      <div className="relative max-w-3xl mx-auto px-6 sm:px-10 py-10">
        <div className="flex items-center justify-between mb-16">
          <Logo size="md" />
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--accent-blue)]">
            Wellness Check-in · Care Recommended
          </div>
        </div>

        <PageHeader
          eyebrow="Thank you for checking in"
          animate={false}
          title={
            <>
              Today, one-to-one support<br />
              <span className="text-cyan-glow">would serve you better.</span>
            </>
          }
          subtitle="Your check-in suggests this self-guided wellness session isn't the best fit for where you're at right now. The Integrated Wellness team has been notified to reach out — you don't have to do anything else here."
        />

        <Card
          strong
          className="border-l-4 fade-up"
          style={{ borderLeftColor: "var(--accent-blue)" }}
          data-testid="referred-team-card"
        >
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--accent-blue)] mb-3">
            What happens next
          </div>
          <p className="text-ink text-base leading-relaxed">
            A member of your wellness team will see your check-in and follow up
            with you directly. If you have an immediate question, you can
            contact the Integrated Wellness office.
          </p>
        </Card>

        <Card
          strong
          className="mt-6 border-l-4 fade-up"
          style={{ borderLeftColor: "var(--danger)" }}
          data-testid="referred-crisis-card"
        >
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--danger)] mb-3">
            If you need help right now
          </div>
          <div className="space-y-3">
            <Resource label="988 Suicide & Crisis Lifeline (US)" value="Call or text 988" />
            <Resource label="Crisis Text Line" value="Text HOME to 741741" />
            <Resource label="If in immediate danger" value="Call 911 / local emergency" />
          </div>
        </Card>

        <div className="mt-8 text-[color:var(--text-secondary)] leading-relaxed text-sm">
          A quiet suggestion while you wait: place both feet flat on the floor,
          take three slow breaths with longer exhales, and look around the room
          — name three things you can see. Small things, gently.
        </div>

        <div className="mt-10 flex items-center gap-4">
          <button
            type="button"
            className="btn-ghost"
            onClick={onRestart}
            data-testid="referred-restart-btn"
          >
            ↩ Back to start
          </button>
        </div>
      </div>
    </Aurora>
  );
}

function Resource({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 border-b border-[color:var(--hairline)] last:border-0">
      <div className="text-[color:var(--text-secondary)] text-sm">{label}</div>
      <div className="font-mono text-cyan-glow text-base sm:text-lg tracking-wide">
        {value}
      </div>
    </div>
  );
}
